/**
 * Glossary for the User Guide.
 *
 * Wherever a term has a number or a list behind it, this module imports
 * the SAME constant the product uses rather than restating it. If a
 * threshold is retuned in code, the manual re-renders with the new
 * value — nobody has to remember to update prose.
 *
 * Terms whose definition is genuinely editorial (what "depletion"
 * means to HY) are written out here, because there is no constant to
 * point at.
 */

import { VISIT_CHANNEL_LABELS } from '../api/field';
import { CONTROL_STATES } from '../lib/controlStates';
import { TODAY_PLAN_LIMIT } from '../pages/field/todayScore';

export interface GlossaryEntry {
  term: string;
  definition: string;
  /** Which product area the term is most used in. */
  area: 'Shared' | 'Depletions' | 'Regional Sales';
}

const CHANNEL_LIST = Object.values(VISIT_CHANNEL_LABELS).join(', ');

export const GLOSSARY: GlossaryEntry[] = [
  {
    term: 'Depletion',
    area: 'Depletions',
    definition:
      'A case leaving a distributor’s warehouse for a retail account — the measure of real demand, as opposed to what the distributor bought from us.',
  },
  {
    term: '6-pack',
    area: 'Shared',
    definition:
      'The unit shown everywhere. Data arrives in 9-litre equivalent cases; one case is two 6-packs, and the dashboard converts on the way out.',
  },
  {
    term: 'Account',
    area: 'Shared',
    definition:
      'A retail buyer — a bar, restaurant, liquor store, or online seller. Not a distributor.',
  },
  {
    term: 'On-Premise',
    area: 'Depletions',
    definition: 'Consumed on site: bars, restaurants, hotels, clubs.',
  },
  {
    term: 'Off-Premise',
    area: 'Depletions',
    definition: 'Sold to take away: liquor stores, package stores, grocery.',
  },
  {
    term: 'Online Retailers',
    area: 'Depletions',
    definition:
      'E-commerce sellers, carved out of Off-Premise rather than added on top — so the four channel shares still sum to the total.',
  },
  {
    term: 'Other',
    area: 'Depletions',
    definition:
      'Accounts with no channel set. A large share usually means new accounts need categorising, not that volume is missing.',
  },
  {
    term: 'Control state (ABC state)',
    area: 'Shared',
    definition: `A state that runs spirits distribution itself, so the liquor board is effectively the buyer and sales run through listings rather than account by account. There are ${CONTROL_STATES.size} — see the key above. Every other state is a license state.`,
  },
  {
    term: 'Follow-up bucket',
    area: 'Shared',
    definition:
      'How long since an account last ordered. Every account sits in exactly one of the five buckets in the key above. Within a bucket, accounts rank by lifetime volume.',
  },
  {
    term: 'Momentum (velocity)',
    area: 'Shared',
    definition:
      'The last three months of volume against the three before, shown as Growing, Declining, Steady, or New activity — see the key above.',
  },
  {
    term: 'Reach and depth',
    area: 'Depletions',
    definition:
      'Reach is how many accounts buy a product; depth is the average 6-pk each of them takes.',
  },
  {
    term: 'Core whiskey gap',
    area: 'Shared',
    definition:
      'An account not carrying both a Rye and a Bourbon (the Bottled in Bond bottling specifically). These are flagged and pushed to the top of the ranked lists.',
  },
  {
    term: "Today's Plan",
    area: 'Regional Sales',
    definition: `A rep’s shortlist for the day — up to ${TODAY_PLAN_LIMIT} accounts, ranked by likely impact. Pinned accounts come first; recently visited ones are held back until their cooldown passes.`,
  },
  {
    term: 'Pin / skip',
    area: 'Regional Sales',
    definition:
      'A pin keeps an account at the top of Today’s Plan until removed. A skip just hides a row for now — it is not saved, so a refresh brings it back.',
  },
  {
    term: 'Visit note',
    area: 'Regional Sales',
    definition: `One contact with an account: a date, a channel (${CHANNEL_LIST}), an outcome, and free text. The outcome also sets a cooldown — see the key above.`,
  },
  {
    term: 'Flag',
    area: 'Regional Sales',
    definition:
      'A request for an admin to fix the account record — closed store, new buyer, wrong details. Distinct from an outcome, which describes a conversation.',
  },
  {
    term: 'Territory',
    area: 'Regional Sales',
    definition:
      'The states a representative covers, assigned by an admin. It decides which accounts they see.',
  },
  {
    term: 'Manually added account',
    area: 'Regional Sales',
    definition:
      'An account typed in by hand. With no depletion history it cannot sit in a recency bucket, so it waits in its own tab until a depletions file confirms it.',
  },
  {
    term: 'Acting as',
    area: 'Regional Sales',
    definition:
      'An admin using the app as a specific rep. A banner stays on screen, and anything logged is saved to that rep.',
  },
];

/** Distinct areas present in the glossary, in display order. */
export const GLOSSARY_AREAS: GlossaryEntry['area'][] = ['Shared', 'Depletions', 'Regional Sales'];
